import React from 'react';
import { C } from '../constants';
import { currentClaims, profileLabel } from '../services/auth';
import { tecnicoEnOnboarding } from '../services/acceso';
import { lineasIngresoTecnico, resumenIngresosTecnico } from '../services/ingresos-tecnico';
import { listarPagosTecnico, verMedioTecnico, guardarMedioTecnico, misVisitas } from '../services/pagos';
import { CURSOS_TECNICO, etiquetaEstadoCurso } from '../data/cursos-tecnico';
import { useDescuentoTecnico } from '../services/descuento-tecnico';
import { useComisionSuite } from '../services/comision';
import VerificacionTecnico from './VerificacionTecnico';

const fmtCOP = (n) => (n != null ? '$' + Math.round(n).toLocaleString('es-CO') : '$0');

const EST_ING = {
  abierto: { label: 'En seguimiento', col: C.muted },
  pendiente: { label: 'Por liquidar', col: C.amber },
  listo: { label: 'Listo para giro', col: C.teal },
  girado: { label: 'Girado', col: '#00c781' },
};

const CAMPO = {
  width: '100%', background: 'transparent', color: 'inherit',
  border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 9px', fontSize: 12,
};

/**
 * A DÓNDE TE GIRAMOS. Sin este dato el giro no sale: marcar «girado» sin destino sería
 * anotar una plata que nadie mandó. Lo registra el técnico, no el admin por él.
 *
 * El componente vive FUERA del que lo usa a propósito: uno declarado dentro del cuerpo de
 * otro es una función nueva en cada render y React remonta el subárbol — el input pierde el
 * foco en cada tecla. Ya pasó en este repo (`componentes-anidados.test.js` lo fija).
 */
function MedioDeCobro({ email, techId }) {
  const [medio, setMedio] = React.useState(null);
  const [cfg, setCfg] = React.useState({ retefuente_pct: 0, soporte_dian: true });
  const [form, setForm] = React.useState({ tipo: 'breb', llave: '', banco: '', cuenta: '', cuenta_tipo: 'ahorros', titular: '', documento: '' });
  const [estado, setEstado] = React.useState('');
  const [abierto, setAbierto] = React.useState(false);
  const [completo, setCompleto] = React.useState(true);

  React.useEffect(() => {
    if (!email) return undefined;
    let vivo = true;
    verMedioTecnico(email).then((r) => {
      if (!vivo || !r) return;
      setCfg({ retefuente_pct: r.retefuente_pct || 0, soporte_dian: r.soporte_dian !== false });
      // 🔒 Sin sesión de la suite el hub-api enmascara la cuenta y la llave (`completo:false`). Se
      // guarda ese dato: el resumen tiene que decir que está OCULTO, porque «Bre-B ••••6589» sin
      // aviso se lee como el dato de verdad — y el formulario no se puede precargar con puntos.
      setCompleto(r.completo !== false);
      if (r.medio) {
        setMedio(r.medio);
        if (r.completo !== false) setForm((f) => ({ ...f, ...r.medio, tipo: r.medio.tipo || 'breb' }));
        else setForm((f) => ({ ...f, tipo: r.medio.tipo || 'breb' }));
      }
    }).catch(() => {});
    return () => { vivo = false; };
  }, [email]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const guardar = async () => {
    setEstado('guardando');
    try {
      const r = await guardarMedioTecnico({ ...form, email, tech_id: techId });
      if (r && r.ok) {
        setMedio(r.medio);
        setAbierto(false);
        setEstado('listo');
      } else {
        setEstado(r?.error || 'No se pudo guardar.');
      }
    } catch (err) {
      setEstado(err.message || 'No se pudo guardar.');
    }
  };

  const resumen = medio
    ? (medio.tipo === 'transferencia'
      ? `${medio.banco} · ${medio.cuenta_tipo || 'ahorros'} ${medio.cuenta}`
      : `${medio.tipo === 'breb' ? 'Bre-B' : 'Nequi'} · ${medio.llave}`)
    : null;

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${medio ? C.teal : C.amber}`, borderRadius: 10, padding: '12px 14px', marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.heading }}>¿A dónde te giramos?</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3, lineHeight: 1.45 }}>
            {resumen
              ? (
                <>
                  Te giramos a <strong style={{ color: C.heading }}>{resumen}</strong>.
                  {!completo && (
                    <> <span style={{ color: C.amber }}>
                      Está oculto: para verlo completo o cambiarlo, entrá con tu sesión de la suite.
                    </span></>
                  )}
                </>
              )
              : 'Registra tu llave Bre-B o tu cuenta. Mientras no esté, el giro del 80% no se puede ejecutar.'}
          </div>
        </div>
        <button type="button" onClick={() => setAbierto((v) => !v)}
          style={{ background: abierto ? 'transparent' : C.teal, color: abierto ? C.muted : C.heading, border: `1px solid ${abierto ? C.border : C.teal}`, borderRadius: 8, padding: '7px 14px', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
          {abierto ? 'Cancelar' : (medio ? 'Cambiar' : 'Registrar medio')}
        </button>
      </div>

      {abierto && (
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
          <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
            Medio
            <select value={form.tipo} onChange={set('tipo')} style={{ ...CAMPO, marginTop: 4 }}>
              <option value="breb">Bre-B (llega al instante)</option>
              <option value="nequi">Nequi</option>
              <option value="transferencia">Cuenta bancaria</option>
            </select>
          </label>
          {form.tipo === 'transferencia' ? (
            <>
              <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
                Banco
                <input value={form.banco || ''} onChange={set('banco')} style={{ ...CAMPO, marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
                Número de cuenta
                <input value={form.cuenta || ''} onChange={set('cuenta')} style={{ ...CAMPO, marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
                Tipo
                <select value={form.cuenta_tipo || 'ahorros'} onChange={set('cuenta_tipo')} style={{ ...CAMPO, marginTop: 4 }}>
                  <option value="ahorros">Ahorros</option>
                  <option value="corriente">Corriente</option>
                </select>
              </label>
            </>
          ) : (
            <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
              {form.tipo === 'breb' ? 'Llave Bre-B' : 'Número Nequi'}
              <input value={form.llave || ''} onChange={set('llave')} style={{ ...CAMPO, marginTop: 4 }} />
            </label>
          )}
          <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
            Titular
            <input value={form.titular || ''} onChange={set('titular')} style={{ ...CAMPO, marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
            Cédula / NIT
            <input value={form.documento || ''} onChange={set('documento')} style={{ ...CAMPO, marginTop: 4 }} />
          </label>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={guardar}
              style={{ background: C.teal, color: C.heading, border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
              Guardar
            </button>
            {estado && estado !== 'listo' && (
              <span style={{ fontSize: 11.5, color: estado === 'guardando' ? C.muted : C.orange }}>
                {estado === 'guardando' ? 'Guardando…' : estado}
              </span>
            )}
          </div>
          <div style={{ gridColumn: '1 / -1', fontSize: 11, color: C.muted, lineHeight: 1.45 }}>
            {/* La cédula no es burocracia: con ella Alebas emite el documento soporte DIAN del
                giro. Sin ese documento el pago no es un costo deducible. */}
            La cédula se usa para emitir tu <strong style={{ color: C.heading }}>documento soporte</strong> (DIAN)
            por la mano de obra{cfg.retefuente_pct > 0 ? <>, con retención en la fuente del {cfg.retefuente_pct}%</> : ' — hoy sin retención'}.
          </div>
        </div>
      )}
    </div>
  );
}

const EST_VISITA = {
  pendiente: { label: 'Sin pagar', col: C.muted, nota: 'El cliente todavía no la pagó.' },
  pagada: { label: 'Pagada · en definición', col: C.amber, nota: 'Si el cliente instala, se le descuenta de la obra; si no, es tuya.' },
  acreditada: { label: 'Fue a la instalación', col: C.teal, nota: 'El cliente cerró: se le descontó de la obra y tú cobras el 80% de la instalación.' },
  liquidada: { label: 'Tuya · girada', col: '#00c781', nota: 'No hubo instalación: quedó como tu ingreso.' },
  anulada: { label: 'Anulada', col: C.muted, nota: '' },
};

/**
 * VISITAS TÉCNICAS. La misma plata tiene dos destinos y el técnico tiene derecho a ver en
 * cuál está la suya: mientras dice «pagada» todavía no se sabe, y eso se dice así en vez de
 * mostrarle un número que después cambia de dueño.
 */
function MisVisitas({ visitas }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.yellow}`, borderRadius: 10, padding: '12px 14px', marginBottom: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: C.heading }}>Visitas técnicas</div>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3, marginBottom: 8, lineHeight: 1.45 }}>
        Lo que el cliente paga por tu visita de validación. Si contrata la instalación se le
        descuenta de la obra; si no la contrata, la visita queda para ti (80%).
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
          <thead>
            <tr>{['Cotización', 'Valor', 'Tu 80%', 'Estado'].map((h) => (
              <th key={h} style={{ textAlign: 'left', fontSize: 9.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, padding: '8px 6px', borderBottom: `1px solid ${C.border}` }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {visitas.slice(0, 10).map((v) => {
              const e = EST_VISITA[v.estado] || EST_VISITA.pendiente;
              return (
                <tr key={v.id} title={e.nota}>
                  <td style={{ fontSize: 12, color: C.heading, fontWeight: 600, padding: '7px 6px' }}>{v.quote_id || '—'}</td>
                  <td style={{ fontSize: 12, color: C.text, padding: '7px 6px' }}>{fmtCOP(v.valor_cop)}</td>
                  <td style={{ fontSize: 12, color: v.estado === 'acreditada' ? C.muted : C.teal, fontWeight: 700, padding: '7px 6px' }}>
                    {v.estado === 'acreditada' ? '—' : fmtCOP(v.neto_tecnico_cop)}
                  </td>
                  <td style={{ padding: '7px 6px' }}>
                    <span style={{ fontSize: 9.5, fontWeight: 700, color: e.col, background: `${e.col}18`, border: `1px solid ${e.col}44`, borderRadius: 5, padding: '2px 8px' }}>{e.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px', borderLeft: `3px solid ${C.teal}` }}>
      <div style={{ fontSize: 9.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: C.heading, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

export default function DashboardTecnico({ go, quotes = [], ordenes = [], sincro = {} }) {
  // El reparto se lo pregunta el hub-api a la gestion: acá no se escribe ningún porcentaje.
  const comision = useComisionSuite('instalacion');
  const descTec = useDescuentoTecnico();
  const claims = currentClaims();
  const nombre = (claims?.name || '').split(' ')[0] || 'Técnico';
  const [pagos, setPagos] = React.useState([]);
  // 🔴 Si esta consulta falla, `pagos` queda vacío y «Neto por liquidar» sale en $0 — o sea, se
  // le dice a un técnico que no tiene nada por cobrar cuando no se pudo preguntar. Y acá las
  // cifras derivadas quedan SUBESTIMADAS, no vacías: por eso el aviso va SOBRE los KPI, no en
  // lugar de ellos. Un cero en una pantalla de plata se lee como un hecho.
  const [pagosFallo, setPagosFallo] = React.useState(false);
  // 🔑 El MOTIVO, no solo el fallo. Desde el 7-sep la ruta exige sesión de la suite (es la plata
  // de una persona), así que el caso más probable ya no es «el servidor no responde» sino «entrá
  // con tu sesión» — y eso el técnico lo puede resolver él si se lo decimos.
  const [pagosMotivo, setPagosMotivo] = React.useState(null);
  const [visitas, setVisitas] = React.useState([]);
  React.useEffect(() => {
    const email = claims?.email;
    const tid = claims?.technician_id ?? claims?.user_id ?? claims?.id;
    if (!email && tid == null) return undefined;
    let vivo = true;
    listarPagosTecnico(email, tid).then((r) => {
      if (!vivo) return;
      if (r && Array.isArray(r.pagos)) { setPagos(r.pagos); setPagosFallo(false); }
      else setPagosFallo(true);
    }).catch((e) => {
      if (!vivo) return;
      setPagosFallo(true);
      setPagosMotivo(e && e.message ? String(e.message) : null);
    });
    return () => { vivo = false; };
  }, [claims?.email, claims?.technician_id, claims?.user_id, claims?.id]);
  const ctx = { quotes, ordenes, pagos };
  const onboarding = tecnicoEnOnboarding(claims, ctx);
  const lineas = lineasIngresoTecnico(claims, ctx);
  const res = resumenIngresosTecnico(lineas);
  const disponibles = CURSOS_TECNICO.filter((c) => c.estado === 'disponible').length;

  const kit = [
    { id: 'estudio', title: 'Estudio de último nivel', desc: '8760 h, sombras, string, P50/P90 y flujo a 25 años. Lo usas para cerrar.', go: 'estudio' },
    { id: 'memoria', title: 'Memoria técnica y unifilar', desc: 'Documentos que salen del estudio para el OR y el cliente.', go: 'estudio' },
    { id: 'misCotizaciones', title: 'Seguimiento de cotizaciones', desc: 'Acompaña cada propuesta hasta el cierre.', go: 'misCotizaciones' },
    { id: 'ordenes', title: 'Órdenes de servicio', desc: 'Instalación, avance y entrega de obra.', go: 'ordenes' },
    { id: 'soporte', title: 'PQR y posventa', desc: 'Tickets de tus clientes, servicio completo.', go: 'soporte' },
    // Solo se promete el descuento si el servidor dice que esta sesión lo va a recibir: hasta el
    // 7-sep la tarjeta ofrecía −15 % y el checkout cobraba lista.
    {
      id: 'tienda',
      title: 'Tienda preferencial',
      desc: descTec.aplica
        ? `−${descTec.pct}% en equipos cuando necesites comprar algo específico para una obra.`
        : 'Equipos para tus obras, con ficha técnica y certificados a la mano.',
      go: 'tienda',
    },
  ];

  return (
    <div className="al-content sh-fade-up" style={{ maxWidth: 1240, margin: '0 auto', padding: '16px 18px 36px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div style={{ borderLeft: `3px solid ${C.teal}`, paddingLeft: 10 }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.heading }}>Tu consola, {nombre}</h1>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
            Ingresos en la plataforma, obras y cursos. {claims && <strong style={{ color: C.heading }}>{profileLabel(claims)}</strong>}
          </div>
        </div>
        <button onClick={() => go && go('estudio')}
          style={{ background: C.teal, color: C.heading, border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
          Abrir kit de cierre →
        </button>
      </div>

      {onboarding && (
        <div style={{ background: `${C.teal}14`, border: `1px solid ${C.teal}44`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: C.heading, lineHeight: 1.45 }}>
          Arranque de <strong>2 meses</strong> con todas las herramientas. Úsalas para cerrar tu primera cotización.
          Después el estudio queda gratis si cierras al menos un proyecto al mes.
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.8, margin: '4px 0 8px' }}>Tus ingresos</div>
      {/* Un cero en una pantalla de plata se lee como un hecho, así que si alguna de las tres
          fuentes de estas cifras no llegó, hay que decirlo ANTES de los números — no en vez de
          ellos: lo que se sabe sigue sirviendo, lo que falta es cuánto falta. */}
      {(pagosFallo || sincro.quotes === 'fallo' || sincro.ordenes === 'fallo') && (
        <div style={{ background: `${C.amber}1A`, border: `1px solid ${C.amber}66`, borderRadius: 8, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: '#7A4A00', lineHeight: 1.45 }}>
          <strong>Estas cifras pueden estar incompletas.</strong> No se pudo consultar{' '}
          {[pagosFallo && 'tus pagos', sincro.quotes === 'fallo' && 'tus cotizaciones', sincro.ordenes === 'fallo' && 'tus órdenes'].filter(Boolean).join(' · ')}.
          {pagosMotivo && <div style={{ marginTop: 6, fontSize: 11 }}>{pagosMotivo}</div>}
          Un valor en cero acá <strong>no significa que no tengas nada por cobrar</strong>.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 8 }}>
        <Kpi label="Neto por liquidar" value={fmtCOP(res.netoPendiente)} hint={`${comision.neto_pct}% de obras ganadas / en curso`} />
        <Kpi label="Listo para giro" value={fmtCOP(res.netoListo)} hint="Obra instalada o cerrada" />
        <Kpi label="Ya girado" value={fmtCOP(res.netoGirado)} hint="Marcado como pagado" />
        <Kpi label="Obras en plataforma" value={String(res.obras)} hint={`${res.abiertas} en seguimiento`} />
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 16, lineHeight: 1.45 }}>
        Tú cobras la instalación. El <strong style={{ color: C.heading }}>{comision.neto_pct}% es tuyo</strong>. El {comision.pct}% no
        es ganancia de Solar Hub: se reinvierte en infraestructura, capacitaciones y las
        herramientas de modelado.
      </div>

      {visitas.length > 0 && <MisVisitas visitas={visitas} />}

      {/* Desde el 1-nov-2026 sin esto no se opera: va antes del medio de cobro porque sin ella
          el giro tampoco sale. */}
      <VerificacionTecnico email={claims?.email} />

      <MedioDeCobro
        email={claims?.email}
        techId={claims?.technician_id ?? claims?.user_id ?? claims?.id}
      />

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '6px 14px 12px', marginBottom: 18, overflowX: 'auto' }}>
        {lineas.length === 0 ? (
          <div style={{ fontSize: 12, color: C.muted, padding: '18px 4px', textAlign: 'center' }}>
            {pagosFallo || sincro.quotes === 'fallo' ? 'No se pudo consultar tus obras. Esto NO significa que no tengas obras asignadas.' : 'Aún no hay obras asignadas. Cierra una cotización y el 80% de la mano de obra aparecerá aquí.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
            <thead>
              <tr>{['Cliente', 'kWp', 'Instalación', `Tu ${comision.neto_pct}%`, `Reinversión ${comision.pct}%`, 'Estado'].map((h) => (
                <th key={h} style={{ textAlign: 'left', fontSize: 9.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, padding: '8px 6px', borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {lineas.slice(0, 12).map((l) => {
                const e = EST_ING[l.estado] || EST_ING.abierto;
                return (
                  <tr key={l.key}>
                    <td style={{ fontSize: 12, color: C.heading, fontWeight: 600, padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>{l.cliente}</td>
                    <td style={{ fontSize: 12, color: C.text, padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>{l.kwp ?? '—'}</td>
                    <td style={{ fontSize: 12, color: C.text, padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>{fmtCOP(l.instalacion)}</td>
                    <td style={{ fontSize: 12, color: C.teal, fontWeight: 700, padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>{fmtCOP(l.neto)}</td>
                    <td style={{ fontSize: 12, color: C.muted, padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>{fmtCOP(l.reinversion)}</td>
                    <td style={{ padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>
                      <span style={{ fontSize: 9.5, fontWeight: 700, color: e.col, background: `${e.col}18`, border: `1px solid ${e.col}44`, borderRadius: 5, padding: '2px 8px' }}>{e.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.8, margin: '4px 0 8px' }}>Kit de último nivel para cerrar</div>
      <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10, lineHeight: 1.45 }}>
        Todas las herramientas de ingeniería y campo para cerrar el negocio: modelado, documentos, seguimiento, obra, PQR y precio preferencial en la Tienda.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 8, marginBottom: 18 }}>
        {kit.map((t) => (
          <button key={t.id} type="button" onClick={() => go && go(t.go)}
            style={{ textAlign: 'left', background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.yellow}`, borderRadius: 8, padding: '12px 14px', cursor: 'pointer' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.heading }}>{t.title}</div>
            <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.4, marginTop: 4 }}>{t.desc}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', margin: '4px 0 8px' }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.8 }}>Tus cursos</div>
        <div style={{ fontSize: 11, color: C.muted }}>{disponibles} disponibles · se cargan con el fondo de capacitaciones</div>
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10, lineHeight: 1.45 }}>
        Aquí se cargan las capacitaciones de Solar Hub. No salen de tu 80%: se pagan con la reinversión
        de cada obra (infra, cursos y herramientas de modelado).
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8 }}>
        {CURSOS_TECNICO.map((c) => {
          const abierto = c.estado === 'disponible';
          return (
            <div key={c.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.eje}</span>
                <span style={{
                  fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                  color: abierto ? '#00c781' : C.amber,
                  background: abierto ? '#00c78118' : `${C.amber}18`,
                  border: `1px solid ${abierto ? '#00c78144' : `${C.amber}44`}`,
                  borderRadius: 5, padding: '2px 7px',
                }}>{etiquetaEstadoCurso(c.estado)}</span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.heading, marginTop: 6 }}>{c.titulo}</div>
              <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.4, marginTop: 4 }}>{c.desc}</div>
              <div style={{ fontSize: 10.5, color: C.muted, marginTop: 8 }}>{c.horas} h</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
